import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { api } from "@/lib/api";
import type { FilterGroup } from "@/lib/filters";
import {
  processQueryParams,
  type QueryParams,
} from "@/lib/process-query-params";
import type { SalespersonMetrics } from "@/types/salesperson-types";

export interface Salesperson {
  person_id: string;
  name: string;
  email: string;
  current_employee_role_id: string | null;
  current_title: string | null;
  team_id: string | null;
  team_name: string | null;
  metrics?: Record<keyof SalespersonMetrics, number | string | undefined>;
}

// SalespersonWithMetrics includes all Salesperson fields plus unnested metrics as top-level properties
export interface SalespersonWithMetrics extends Omit<Salesperson, "metrics"> {
  // Allow any metric key from SalespersonMetrics as a property
  [key: string]: string | number | null | undefined | object;
}

export interface SalespeopleResponse {
  results: Salesperson[];
  total: number;
  limit: number;
  offset: number;
}

export function useSalespeople({
  search = "",
  limit = 50,
  offset = 0,
}: {
  search?: string;
  limit?: number;
  offset?: number;
} = {}) {
  return useQuery({
    queryKey: ["salespeople", { search, limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("limit", limit.toString());
      params.append("offset", offset.toString());

      return api.get<SalespeopleResponse>(
        `datahub/salespeople/?${params.toString()}`,
      );
    },
  });
}

export function useSalesperson(personId: string) {
  return useQuery({
    queryKey: ["salesperson", personId],
    queryFn: () => api.get<Salesperson>(`datahub/salespeople/${personId}/`),
    enabled: !!personId,
  });
}

// Extend the shared QueryParams interface for salesperson-specific parameters
export interface SalespersonParams extends QueryParams {
  metrics?: Array<keyof SalespersonMetrics>;
  metric_filters?: {
    account_filters?: FilterGroup;
    opportunity_filters?: FilterGroup;
    touchpoint_filters?: FilterGroup;
  };
}

export function useInfiniteSalespeople(params?: SalespersonParams) {
  const {
    limit = 50,
    sort_by = "name",
    sort_direction = "asc",
    search = "",
    metrics = [],
    ...filterParams
  } = params || {};

  const debouncedSearch = useDebounce(search, 300);

  return useInfiniteQuery({
    queryKey: [
      "infinite-salespeople",
      {
        sort_by,
        sort_direction,
        search: debouncedSearch,
        metrics,
        ...filterParams,
      },
    ],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === "number" ? pageParam * limit : 0;

      // Create base params
      const queryParams: SalespersonParams = {
        limit,
        offset,
        sort_by,
        sort_direction,
        search: debouncedSearch || undefined,
        ...filterParams,
      };

      // Add metrics parameters
      if (metrics.length > 0) {
        queryParams.metrics = metrics;
      }

      // Process all parameters into a URLSearchParams object
      const searchParams = processQueryParams(queryParams);

      return api.get<SalespeopleResponse>(
        `datahub/salespeople/?${searchParams}`,
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length;
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page.results.length,
        0,
      );

      return totalFetched < lastPage.total ? nextPage : undefined;
    },
  });
}

/**
 * Hook for fetching all salespeople matching the given filters for CSV export
 * This leverages the same API endpoint and parameter structure as useInfiniteSalespeople
 * Returns a function that can be called to trigger the export, rather than fetching immediately
 */
export function useSalespeopleExport() {
  const mutation = useMutation<
    SalespersonWithMetrics[],
    Error,
    SalespersonParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      const allSalespeople: Salesperson[] = [];
      let offset = 0;
      let hasMoreData = true;

      // Paginate through all salespeople
      while (hasMoreData) {
        // Create query params for this page
        const queryParams: SalespersonParams = {
          ...params,
          limit: 500, // Override the limit that is passed in
          offset,
        };

        // Add metrics parameters if they exist
        if (params.metrics && params.metrics.length > 0) {
          queryParams.metrics = params.metrics;
        }

        // Add metric_filters if present
        if (
          params.metric_filters &&
          Object.keys(params.metric_filters).length > 0
        ) {
          queryParams.metric_filters = params.metric_filters;
        }

        // Process parameters into a URLSearchParams object
        const searchParams = processQueryParams(queryParams);

        // Fetch the current page of salespeople
        const response = await api.get<SalespeopleResponse>(
          `datahub/salespeople/?${searchParams}`,
        );

        // Add the results to our array
        allSalespeople.push(...response.results);

        // Check if we should continue fetching
        hasMoreData = allSalespeople.length < response.total;
        // Increment the offset for the next page
        offset += response.results.length;
      }

      // Transform Salesperson[] to SalespersonWithMetrics[] by unnesting metrics
      return allSalespeople.map((salesperson) => {
        const { metrics, ...baseObject } = salesperson;

        // Create a new object with all base properties
        const salespersonWithMetrics: SalespersonWithMetrics = {
          ...baseObject,
        };

        // Add each metric as a top-level property if metrics exist
        if (metrics) {
          for (const [key, value] of Object.entries(metrics)) {
            salespersonWithMetrics[`metric_${key}`] = value;
          }
        }

        return salespersonWithMetrics;
      });
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportSalespeople: (params?: SalespersonParams) => mutation.mutate(params),
    exportSalespeopleAsync: (params?: SalespersonParams) =>
      mutation.mutateAsync(params),
  };
}
